import {
  LBButton,
  LBInfoCircleIcon,
  LBSeparator,
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger
} from '@linearb/buzz'
import { useState } from 'react'

import { formatDateWithRelative } from '../../utils/dateHelpers'

export interface Champion {
  name?: string | null
  title?: string | null
  email?: string | null
  last_seen_at?: string | null
}

interface ChampionsListProps {
  champions: Champion[]
}

const PREVIEW_COUNT = 3

const ChampionsList = ({ champions }: ChampionsListProps) => {
  const [expanded, setExpanded] = useState(false)

  if (champions.length === 0) {
    return null
  }

  const overflow = Math.max(0, champions.length - PREVIEW_COUNT)
  const shown = expanded ? champions : champions.slice(0, PREVIEW_COUNT)

  return (
    <div className='rounded-md border border-border bg-muted/30 p-4 space-y-3'>
      <div className='flex items-baseline justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-semibold uppercase tracking-wide text-muted-foreground'>
            Champions
          </h3>
          <LBTooltip>
            <LBTooltipTrigger asChild>
              <button
                type='button'
                aria-label='What is a champion'
                className='cursor-help text-muted-foreground/70'
              >
                <LBInfoCircleIcon aria-hidden='true' className='h-3.5 w-3.5' />
              </button>
            </LBTooltipTrigger>
            <LBTooltipContent className='max-w-xs'>
              <p>
                Admins who visited the User Management settings on more than
                one distinct day in the last 90 days.
              </p>
            </LBTooltipContent>
          </LBTooltip>
        </div>
        <span className='text-xs text-muted-foreground'>
          Settings → Users · last 90 days
        </span>
      </div>

      <ul className='space-y-3'>
        {shown.map((champion, index) => {
          const hasName = champion.name && champion.name !== champion.email
          const lastSeen = champion.last_seen_at
            ? formatDateWithRelative(champion.last_seen_at)
            : null
          return (
            <li
              key={champion.email ?? `${champion.name ?? 'champion'}-${index}`}
              className='text-sm leading-snug'
            >
              {hasName && (
                <div className='font-medium text-foreground'>
                  {champion.name}
                  {champion.title && (
                    <span className='ml-2 font-normal text-muted-foreground'>
                      ({champion.title})
                    </span>
                  )}
                </div>
              )}
              {!hasName && champion.title && (
                <div className='text-muted-foreground'>{champion.title}</div>
              )}
              {champion.email && (
                <a
                  href={`mailto:${champion.email}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-sm text-primary hover:underline break-all'
                >
                  {champion.email}
                </a>
              )}
              {lastSeen && (
                <div className='text-xs text-muted-foreground mt-0.5'>
                  Last visit: {lastSeen}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {overflow > 0 && (
        <>
          <LBSeparator />
          <LBButton
            variant='ghost'
            size='sm'
            onClick={() => setExpanded(prev => !prev)}
            className='h-7 px-2 text-xs'
          >
            {expanded ? 'Show less' : `Show ${overflow} more`}
          </LBButton>
        </>
      )}
    </div>
  )
}

export default ChampionsList
